export const enDataHub = {
  breadcrumb: {
    appStudio: "App Studio",
    dataHub: "Data Hub",
    aria: "Breadcrumb",
  },
  title: "Data Hub",
  subtitle: "Connect and manage your data sources",
  subtitleDomains: "Organize datasets into domains for AI context",
  tabs: {
    connections: "Connections",
    domains: "Domains",
    aria: "Data hub sections",
  },
  actions: {
    newConnection: "New Connection",
    newDomain: "New Domain",
  },
  deleteConnectionConfirm: {
    title: "Delete connection?",
    message:
      "This connection will be permanently deleted. Any domains built on it — and everything in them (apps, nuggets, semantic models) — will be deleted too. This cannot be undone.",
    cancel: "Cancel",
    confirm: "Delete",
    closeAria: "Close",
    errors: {
      deleteFailed: "Could not delete the connection.",
    },
  },
  domains: {
    unknownConnection: "Unknown connection",
    menuAria: "Actions for {{name}}",
    connectionLabel: "Connection: {{name}}",
    exportAction: "Export",
    exportFailed: "Could not export the domain. Please try again.",
  },
  domainDetail: {
    tablesLibrary: {
      title: "Tables Library",
      viewOnlyNote: "Browse the tables in this domain.",
      subtitle: "Select and move the tables to canvas",
      columnCount: "{{count}} columns",
      tableCount: "{{count}} tables",
      schemaCount: "{{count}} schemas",
      columnCheckboxAria: "Include {{column}} from {{table}}",
      searchPlaceholder: "Search databases, schemas and tables",
      searchAria: "Search databases, schemas and tables",
      noSearchResults: "No databases, schemas or tables match your search.",
    },
    backAria: "Back to Data Hub",
    loadFailed: "Could not load domain tables.",
    retry: "Retry",
    notFound: "Domain not found",
    mode: {
      aria: "Domain entity mode",
      draft: "Draft",
      published: "Published",
    },
    publish: {
      action: "Publish",
      moreOptionsAria: "More publish options",
      withoutAiAction: "Publish without AI generation",
      withoutAiHelp:
        "Publish the selected tables and build the semantic model by hand.",
      success: "Published successfully",
      failed: "Semantic model generation failed. Reverted to draft.",
    },
    discardDraft: {
      action: "Discard draft",
    },
    overview: {
      tab: "Overview",
      loading: "Loading business context",
      fields: {
        domainPurpose: "Domain purpose",
        stakeholders: "Stakeholders",
      },
      placeholders: {
        domainPurpose: "What this dataset is for and who uses it",
        stakeholders: "Type a stakeholder and press Enter",
      },
      actions: {
        save: "Save",
        saveSucceeded: "Business context saved.",
      },
      errors: {
        loadFailed: "Unable to load business context.",
        saveFailed: "Unable to save business context.",
      },
    },
    nuggetConfig: {
      tab: "Nugget Configs",
      title: "Nugget configs",
      subtitle:
        "What the nugget pipeline looks at, over which window, and how often it runs.",
      newTitle: "New nugget config",
      editTitle: "Edit nugget config",
      loading: "Loading nugget configs",
      empty: "No nugget configs yet.",
      emptyHint:
        "A config picks the candidates a nugget run scans, the window of data it reads, and " +
        "how often it runs on its own.",
      sections: {
        basics: "Basics",
        whatEachRunLooksAt: "What each run looks at",
        whenToRun: "When to run",
      },
      emptySchedules:
        "No frequency set — this config will not run on a schedule.",
      scheduleToggleAria: "Run {{name}} on a schedule",
      infoAria: "About {{field}}",
      fields: {
        name: "Name",
        description: "Description",
        focusAreas: "Focus areas",
        timeframe: "Timeframe",
        timeframeMode: "Timeframe mode",
        amount: "Last",
        unit: "Unit",
        startDate: "From",
        endDate: "To",
        schedules: "Frequency",
        kind: "Repeats",
        dayOfWeek: "On",
        dayOfMonth: "Day",
        monthDayMode: "Which day",
        runAtTime: "At (UTC)",
        semanticMateriality: "Judge materiality from the semantic model",
        queryTimeout: "Query timeout (seconds)",
        candidateSeeding: "Scan targets",
        candidates: "Candidates to scan",
        scheduleEnabled: "Run on a schedule",
        interval: {
          daily: "Every N days",
          weekly: "Every N weeks",
          monthly: "Every N months",
          quarterly: "Every N quarters",
        },
      },
      placeholders: {
        name: "e.g. Weekly exec digest",
        description: "What this config is for",
        focusAreas: "Type a focus area and press Enter",
        queryTimeout: "Server default",
      },
      help: {
        focusAreas:
          "Areas the pipeline should prioritise, e.g. revenue, churn.",
        timeframe: "Which window of domain data each run should read.",
        localTime: "{{time}} in your local time ({{zone}})",
        semanticMateriality:
          "Uses each metric's declared unit and its own scale to decide what counts as a " +
          "material move. Turn off to compare against the previous name-based thresholds.",
        queryTimeout:
          "How long a single warehouse query may run before this config's runs give up on " +
          "it. Leave blank to follow the server default. Raising it lets a heavy aggregate " +
          "finish, but a slow query holds one of the run's parallel slots the whole time.",
        candidateSeeding: {
          off:
            "Every metric is scanned against every dimension it can be broken down by. " +
            "Candidates in the semantic model do not change what gets looked at.",
          only:
            "Scans only what the candidates you tick below ask for. Much narrower and faster, " +
            "and anything they do not mention is not looked at this run.",
        },
        candidates:
          "A run scans only what the candidates you tick ask for — anything they do not mention " +
          "is not looked at. Only approved and hand-added candidates can be scanned, and editing " +
          "a candidate in the semantic model changes what this config scans.",
        scheduleEnabledOn:
          "This config runs by itself at the frequencies below.",
        scheduleEnabledNoFrequency:
          "Add a frequency above before this can be turned on. Without one there is nothing " +
          "for the scheduler to fire.",
        scheduleEnabledOff:
          "This config only runs when you start it by hand. The frequencies below are saved " +
          "either way.",
      },
      candidateSeedingModes: {
        off: "Everything (default)",
        only: "Selected candidates only",
      },
      candidates: {
        selectAll_one: "Select all ({{count}} candidate)",
        selectAll_other: "Select all ({{count}} candidates)",
        selectAllMatches_one: "Select the {{count}} match",
        selectAllMatches_other: "Select all {{count}} matches",
        selectedCount: "{{selected}} of {{total}} selected",
        searchPlaceholder: "Search candidates",
        searchAria: "Search candidates",
        noMatches: "No candidate matches \u201c{{query}}\u201d.",
        none: "No approved candidates on this domain yet",
        metrics: "Metrics",
        dimensions: "Dimensions",
        noQuestion: "Untitled candidate",
        noMetrics: "none",
        noDimensions: "none declared — the usual breakdowns are used",
      },
      timeframeModes: {
        relative: "Relative",
        absolute: "Absolute",
      },
      timeframePresets: {
        last_1_day: "Last 1 day",
        last_7_days: "Last 7 days",
        last_30_days: "Last 30 days",
        custom: "Custom…",
      },
      units: {
        hour: "Hours",
        day: "Days",
        week: "Weeks",
        month: "Months",
        quarter: "Quarters",
        year: "Years",
      },
      unitsPlural: {
        hour_one: "hour",
        hour_other: "hours",
        day_one: "day",
        day_other: "days",
        week_one: "week",
        week_other: "weeks",
        month_one: "month",
        month_other: "months",
        quarter_one: "quarter",
        quarter_other: "quarters",
        year_one: "year",
        year_other: "years",
      },
      scheduleKinds: {
        daily: "Daily",
        weekly: "Weekly",
        monthly: "Monthly",
        quarterly: "Quarterly",
      },
      schedulePresets: {
        every_day: "Every day",
        every_week: "Every Monday",
        every_2_weeks: "Every 2 weeks on Monday",
        first_of_month: "First day of every month",
        last_of_month: "Last day of every month",
        first_of_quarter: "First day of every quarter",
        last_of_quarter: "Last day of every quarter",
      },
      monthDayModes: {
        day: "On a set day",
        last: "Last day",
      },
      weekdays: {
        monday: "Monday",
        tuesday: "Tuesday",
        wednesday: "Wednesday",
        thursday: "Thursday",
        friday: "Friday",
        saturday: "Saturday",
        sunday: "Sunday",
      },
      summary: {
        scanScopeAll: "Scans everything",
        scanScope: "Scans {{selected}} of {{total}} candidates",
        scanScopeCount_one: "Scans {{count}} candidate",
        scanScopeCount_other: "Scans {{count}} candidates",
        rolling: "Last {{amount}} {{unit}}",
        absolute: "{{start}} to {{end}}",
        schedule: {
          daily_one: "Every day",
          daily_other: "Every {{count}} days",
          weekly_one: "Every {{weekday}}",
          weekly_other: "Every {{count}} weeks on {{weekday}}",
          monthlyDay_one: "Day {{day}} of every month",
          monthlyDay_other: "Day {{day}} of every {{count}} months",
          monthlyLast_one: "Last day of every month",
          monthlyLast_other: "Last day of every {{count}} months",
          quarterlyDay_one: "Day {{day}} of every quarter",
          quarterlyDay_other: "Day {{day}} of every {{count}} quarters",
          quarterlyLast_one: "Last day of every quarter",
          quarterlyLast_other: "Last day of every {{count}} quarters",
          atTime: "{{when}} at {{time}} UTC",
        },
      },
      deleteDialog: {
        heading: "Delete {{name}}?",
        subheading:
          "This removes the config, its schedules and its candidate selection. Nuggets it has " +
          "already produced are kept. This cannot be undone.",
        confirm: "Delete config",
      },
      actions: {
        newConfig: "New config",
        firstConfig: "Create your first config",
        addSchedule: "Add frequency",
        removeSchedule: "Remove frequency",
        create: "Create config",
        save: "Save config",
        edit: "Edit config",
        delete: "Delete config",
        cancel: "Cancel",
      },
      run: {
        start: "Run now",
        starting: "Starting…",
        streaming: "Run started. Streaming live progress…",
        hint: "Run this config, or pick a previous run to inspect its steps.",
        complete:
          "Run complete — {{signals}} signals, {{stories}} story candidates, " +
          "{{nuggets}} nuggets published.",
        completeCatalog:
          "{{count}} nugget(s) saved to the catalog. Open the Nuggets page to review them.",
        failed: "Run failed: {{message}}",
      },
      errors: {
        loadFailed: "Unable to load nugget configs.",
        nameRequired: "Name is required.",
        candidatesRequired:
          "Pick at least one candidate, or switch the scan targets to everything.",
        queryTimeoutRange:
          "Enter a whole number of seconds between {{min}} and {{max}}, or leave blank for " +
          "the server default.",
        duplicateSchedule:
          "Two frequencies are identical — remove or change one before saving.",
        absoluteRecurring:
          "An absolute date range means every scheduled run reads the same fixed window.",
        toggleFailed: "Unable to change the schedule.",
      },
    },
    learning: {
      tab: "Learning",
      title: "Learning",
      subtitle:
        "What Olive has learned about this domain from its users' Learning History. Only approved learnings are used in chat answers.",
      info: {
        ariaLabel: "How domain learnings are made",
        nightly:
          "Updated every night: what users learned in this domain, with the same learning from several users combined into one.",
        userWins: "When a learning disagrees with a user's own preference, the user's preference wins.",
      },
      filters: {
        aria: "Filter learnings by status",
        pending: "Pending ({{count}})",
        approved: "Approved ({{count}})",
        rejected: "Rejected ({{count}})",
      },
      categories: {
        term_meaning: "Term meaning",
        chat_pattern: "Chat pattern",
        answer_feedback: "Answer feedback",
        nugget_feedback: "Nugget feedback",
      },
      card: {
        backedBy_one: "Backed by {{count}} user",
        backedBy_other: "Backed by {{count}} users",
        interactions_one: "{{count}} interaction",
        interactions_other: "{{count}} interactions",
        reviewedBy: "Reviewed by {{reviewer}}",
        viewEvidenceAria: "View evidence for {{title}}",
        revisionTitle: "Updated wording suggested",
        revisionHint: "Newer user learnings suggest this wording. The approved wording stays in use until you accept it.",
      },
      actions: {
        approve: "Approve",
        editApprove: "Edit & approve",
        edit: "Edit",
        reject: "Reject",
        restore: "Approve",
        acceptRevision: "Accept new wording",
        dismissRevision: "Keep current",
        approveAll: "Approve all",
        rejectAll: "Reject all",
      },
      editDialog: {
        title: "Edit and approve learning",
        closeAria: "Close",
        titleLabel: "Title",
        summaryLabel: "Summary",
        detailLabel: "Details",
        required: "Required",
        titleTooLong: "Keep the title under 120 characters",
        cancel: "Cancel",
        save: "Approve",
      },
      loading: "Loading learnings…",
      empty: {
        pending: "Nothing waiting for review.",
        approved: "No learnings approved yet.",
        rejected: "No rejected learnings.",
      },
      errors: {
        loadFailed: "Unable to load learnings.",
      },
    },
    jobs: {
      tab: "Jobs",
      title: "Jobs",
      subtitle:
        "Scheduled work defined against this domain. Jobs run when you start them.",
      loading: "Loading jobs…",
      empty: "No jobs have been created for this domain yet.",
      lastRun: "Last run: {{status}} · {{when}}",
      neverRun: "Never run",
      actions: {
        newJob: "Create Job",
        runNow: "Run now",
        refreshRuns: "Refresh run status",
        delete: "Delete job",
      },
      errors: {
        loadFailed: "Unable to load jobs.",
      },
      // Shared by the create/edit modal and the job list's quick toggle: one cadence, described
      // once. Times are UTC because the scheduler has no per-tenant timezone -- the form says so
      // and shows the viewer's own equivalent beside it.
      schedule: {
        section: "Schedule",
        enable: "Run on a schedule",
        enableAria: "Run this job on a schedule",
        enabledHint: "This job runs automatically at the times below.",
        disabledHint:
          "This job only runs when you start it by hand. The schedule is saved either way.",
        frequency: "Frequency",
        presets: {
          hourly: "Hourly",
          daily: "Daily",
          weekly: "Weekly",
          monthly: "Monthly",
          advanced: "Advanced (cron)",
        },
        weekdays: {
          monday: "Monday",
          tuesday: "Tuesday",
          wednesday: "Wednesday",
          thursday: "Thursday",
          friday: "Friday",
          saturday: "Saturday",
          sunday: "Sunday",
        },
        minute: "Minutes past the hour",
        weekday: "Day of week",
        dayOfMonth: "Day of month",
        // 31 is a real choice with a real consequence, and the alternative -- silently capping it
        // to the month's length -- would be a different schedule than the one that was asked for.
        dayOfMonthHint:
          "A day later than a month is long is skipped that month.",
        timeOfDay: "Time (UTC)",
        localHint: "{{time}} in your time zone ({{zone}}).",
        expression: "Cron expression (UTC)",
        expressionHint:
          "Five fields: minute, hour, day of month, month, day of week.",
        preview: "Runs as: {{expression}}",
        invalid: "This does not look like a five-field cron expression.",
        toggleAria: "Run this job on a schedule",
        toggleFailed: "Unable to change the schedule.",
      },
      createModal: {
        title: "Create job",
        closeAria: "Close create job dialog",
        jobTypeHint: "What this job does when it runs.",
        fields: {
          jobType: "Job type",
          name: "Job name",
          description: "Description",
        },
        actions: {
          cancel: "Cancel",
          create: "Create job",
        },
        validation: {
          required: "This field is required.",
        },
        errors: {
          saveFailed: "Unable to create the job.",
        },
      },
      editModal: {
        title: "Edit job",
        // Shown in place of the create hint: the update request carries no job type, so the field
        // is locked rather than silently ignoring a change.
        jobTypeLocked: "The job type cannot be changed after a job is created.",
        openAria: "Edit job",
        actions: {
          save: "Save changes",
        },
      },
      // Copy specific to one job type. A new type adds a sibling block here; everything above is
      // shared by every type.
      types: {
        classification: {
          label: "Classification Job",
          outputLabel: "Output: {{name}}",
          unknownConnection: "Unknown connection",
          previewHint: "Preview only — this does not write to S3.",
          outputHint: "Results are written here as a JSON file, one per run.",
          outputPathHint:
            "Optional folder under the connection's own prefix, e.g. forums/classified/. Leave blank to write to the prefix itself.",
          textSettingsChanged:
            "Text settings changed — regenerate example embeddings after saving, or the next run will refuse to start.",
          idLabel: "ID: {{column}}",
          outputPathLabel: "Folder: {{path}}",
          taxonomyQueryLabel: "Taxonomy: from query",
          noS3Connections:
            "Add an S3 connection in Data Hub → Connections to choose an output.",
          taxonomyQueryHint:
            "Optional. Reads the taxonomy from a table instead of the uploaded file. Return ID, LEVEL_1, LEVEL_2, LEVEL_3 and STATUS, and optionally NAME, DESCRIPTION, INCLUSIONS, EXCLUSIONS. Only APPROVED rows are classified into. Output then carries FANNEED_ID instead of the category names.",
          proposedTaxonomyPathHint:
            "Optional folder under the output connection's prefix, e.g. olive/classification/fanneed/. New proposed categories are written there as PROPOSED rows, and each case carries the row's ID as PROPOSED_FANNEED_ID. The taxonomy query must then return every row, not only APPROVED ones.",
          fields: {
            query: "Query",
            taxonomyQuery: "Taxonomy query",
            proposedTaxonomyPath: "Proposed categories folder",
            output: "Output S3 connection",
            outputPath: "Output folder",
            topK: "Examples per record",
            invention: "Propose new categories",
            inventionThreshold: "Propose below confidence",
            reviewThreshold: "Send for review below confidence",
          },
          topKHint:
            "How many similar labelled examples the model is shown. More costs more per record.",
          inventionHint:
            "When no existing category fits a record confidently, ask for a new one. Proposals are collected for review and never used as labels.",
          inventionThresholdHint:
            "Records the model is less confident than this about are treated as evidence the taxonomy has a gap.",
          reviewThresholdHint:
            "A record whose label the model changes with less confidence than this is marked Needs review instead of being trusted. 0 never sends anything for review; 1 sends every changed label.",
          validation: {
            topK: "Enter a whole number from 0 to 50.",
            inventionThreshold: "Enter a number from 0 to 1.",
            reviewThreshold: "Enter a number from 0 to 1.",
            outputPath: "Enter a relative folder, without .. or ://.",
            proposedTaxonomyPath: "Enter a relative folder, without .. or ://.",
          },
          actions: {
            run: "Run",
            rerun: "Run again",
            detectColumns: "Detect columns",
          },
          // The input section: which query result columns the job reads.
          input: {
            heading: "Input columns",
            columnsNone: "Detect columns to choose from the query's result.",
            columnsDetected_one: "{{count}} column detected.",
            columnsDetected_other: "{{count}} columns detected.",
            columnsStale: "The query changed since columns were detected. Detect them again.",
            noColumns: "Detect columns first",
            none: "None",
            idColumn: "ID column",
            idName: "ID name",
            idHint: "Unique per record. Keys the output and drives ${END} batching.",
            nameHint: "What the model sees. Also the key read from example files.",
            productColumn: "Product column",
            productName: "Product name",
            productHint: "Optional. Groups examples for retrieval; copied into the output.",
            textFieldsHeading: "Text fields",
            textFieldsHint:
              "Most important first; drag to reorder. Each name is what the model sees and the key read from example files.",
            textFieldN: "Text field {{position}}",
            textColumn: "Text {{position}} column",
            textName: "Text {{position}} name",
            includeRule: "Text {{position}} include",
            includeAlways: "Always",
            includeMissing: "Only if {{field}} is missing",
            includeShorterThan: "Only if {{field}} is shorter than N characters",
            shorterThan: "Text {{position}} N characters",
            condense: "Condense text {{position}}",
            condenseHint:
              "If longer than the embedding limit (~7,800 bytes), summarize it with one model call first.",
            dragAria: "Reorder text field {{position}}",
            removeTextField: "Remove text field {{position}}",
            addTextField: "Add text field",
            labelsHeading: "Existing label columns",
            labelsHint: "Optional. Columns holding labels the records already have, level 1 first.",
            labelLevel: "Level {{level}} label column",
            instructions: "Instructions",
            instructionsHint: "Optional. Added to the classifier prompt.",
            validation: {
              columnTooLong: "Use a column of at most {{columnMax}} characters.",
              nameTooLong: "Use a name of at most {{nameMax}} characters.",
              duplicateColumn: "Another text field already reads this column.",
              duplicateName: "Another text field already uses this name.",
              invalidRule: "The rule must test a text field above this one.",
              shorterThan: "Enter a whole number of at least 1.",
              notInQuery: "The query's result has no such column.",
              instructionsTooLong: "Use at most {{instructionsMax}} characters.",
            },
          },
          results: {
            count: "{{count}} row returned.",
            count_other: "{{count}} rows returned.",
            truncated: "Showing {{shown}} of {{total}} rows.",
            empty: "The query returned no rows.",
          },
          errors: {
            previewFailed: "Unable to run the query.",
            columnCheckFailed: "Could not check the query's columns: {{message}}",
            columnsMissing: "Some configured columns are not in the query's result.",
          },
        },
        copy: {
          label: "Copy Job",
          sourceFoldersHint:
            "One folder per line, relative to {{root}} in this tenant's storage. Every JSON file under a folder is included, including files added later.",
          sourceFilesHint: "One JSON file per line, relative to the same root.",
          destinationHint:
            "Each source file is copied here unchanged, under its own file name.",
          noS3Connections:
            "Add an S3 connection in Data Hub → Connections to choose a destination.",
          destinationFolderHint:
            "A folder inside the connection's own path. Leave blank to write at the connection's path itself.",
          filesPerRunHint:
            "Copy this many files per run, in file name order, starting after the last file copied. Leave blank to copy everything every run.",
          deleteSourceHint:
            "Source files are kept after copying.",
          deleteSourceWarning:
            "Source files are deleted after each successful copy. This cannot be undone.",
          fields: {
            sourceFolders: "Source folders",
            sourceFiles: "Source files",
            destination: "Destination S3 connection",
            destinationFolder: "Destination folder",
            filesPerRun: "Files per run",
            deleteSource: "Delete source files after copying",
          },
          validation: {
            sourceFolders: "Enter relative folder paths, without .. or a URL.",
            sourceFiles: "Enter relative paths to .json files, without .. or a URL.",
            destinationFolder: "Enter a relative folder path, without .. or a URL.",
            filesPerRun: "Enter a whole number from 1 to 1000, or leave it blank.",
          },
        },
        appSnapshot: {
          label: "App Snapshot Job",
          appLabel: "App: {{name}}",
          unknownApp: "Unknown app",
          appHint:
            "Only published apps can be captured. Publishing state is checked again on each run.",
          namePrefixHint:
            'The fixed part of the snapshot name, including any wording such as "Week". The ISO date from the format below is added after it.',
          namePreview: "Each run is saved as: {{name}}",
          namePreviewPlaceholder: "WBR - Week",
          noPublishedApps:
            "No published apps in this domain yet. Publish one in App Studio to schedule snapshots.",
          fields: {
            app: "App",
            namePrefix: "Name prefix",
            nameFormat: "Name format",
          },
          validation: {
            // Only the "range" case is namespaced; a missing app resolves to the shared
            // "required" message, so it needs no key of its own here.
            namePrefix: "Enter a name prefix of up to 120 characters.",
          },
        },
      },
      classification: {
        heading: "Reference data",
        description:
          "What this job classifies against. Upload one taxonomy file, and one or more files of already-labelled example records. JSON files only. Each classification job keeps its own set.",
        expand: "Show reference data",
        collapse: "Hide reference data",
        approvals: {
          heading: "Approved rows",
          description:
            "Rows a reviewer has approved on a table widget are delivered to this job's output connection each run, alongside the rows it classifies. They are removed from storage once delivered.",
          label: "Deliver approved rows from",
          option: "{{app}} — {{widget}}",
          none: "Not configured",
          hint: "Leave unset to deliver classified rows only.",
          noWidgets:
            "No approval widgets in this domain yet. Turn on approvals for a table widget in App Studio.",
          unavailable: "Previously selected widget ({{value}})",
          missingWidget:
            "This widget is no longer available — it may have been deleted, or had approvals turned off. Runs will fail until this is changed.",
        },
        proposalReview: {
          heading: "Proposed categories",
          description:
            "Records the run could not fit into the taxonomy, shown with the category it suggested adding. Reviewed first: one decision here settles every record that fell into the same gap. Whatever is left over is worked in the approval stage below.",
          label: "Review proposed categories on",
          option: "{{app}} — {{widget}}",
          none: "Not configured",
          hint: "Leave unset to send every record straight to the approval stage.",
          noWidgets:
            "No approval widgets available for this stage. Turn on approvals for a table widget in App Studio, or free up the one already used for approved rows.",
          unavailable: "Previously selected widget ({{value}})",
          missingWidget:
            "This widget is no longer available — it may have been deleted, or had approvals turned off. Runs will fail until this is changed.",
        },
        taxonomy: {
          heading: "Taxonomy",
          description:
            "The label tree records are classified into. Stored as uploaded.",
          empty: "No taxonomy file has been uploaded yet.",
          add: "Add taxonomy",
          replace: "Replace",
          proposals:
            "Categories runs have suggested adding, because nothing in the taxonomy fitted a record. Written by the job, last updated {{timestamp}}.",
        },
        examples: {
          heading: "Examples",
          description:
            "Already-labelled records the classifier matches against — typically one file per product. File names are kept as uploaded and are yours to choose. Up to {{limit}} files at a time.",
          empty: "No example files have been uploaded yet.",
          addFiles: "Add example file(s)",
          count_one: "{{count}} file",
          count_other: "{{count}} files",
        },
        uploading: "Uploading…",
        uploadSucceeded: "Files uploaded.",
        table: {
          fileName: "File name",
          addedAt: "Added",
          lastModifiedAt: "Last modified",
          actions: "Actions",
          ariaLabel: "Classification example files",
        },
        embedding: {
          action: "Generate embeddings",
          regenerate: "Regenerate embeddings",
          running: "Generating…",
          started: "Embedding started. This usually takes under a minute.",
          // One state covers the whole example set: every vector lives in a single stored document,
          // so there is nothing per-file to report.
          state: {
            no_examples: "Nothing to embed",
            not_embedded: "Not embedded",
            embedded: "Embedded",
            stale: "Out of date",
          },
          embeddedAt: "Last generated {{timestamp}}",
          staleHint:
            "Example files changed since the embeddings were generated.",
        },
        deleteAria: "Delete {{filename}}",
        deleteDialog: {
          heading: "Delete file?",
          subheading:
            "{{filename}} will be permanently removed from storage. This cannot be undone.",
          // Deleting an example file drops the whole embeddings document, because a case text can be
          // shared between files — the next run rebuilds it from what is left.
          examplesWarning:
            "The generated embeddings will be cleared and must be regenerated.",
          cancel: "Cancel",
          confirm: "Delete File",
        },
        errors: {
          someFilesFailed: "These files were not uploaded:",
          fileLine: "{{filename}} — {{message}}",
          notJson: "Only JSON files are supported.",
          tooLarge: "The file is larger than {{maxMb}} MB.",
          tooManyFiles:
            "You selected {{count}} files. Add at most {{limit}} files at a time.",
        },
      },
    },
    documents: {
      tab: "Documents",
      description:
        "PDF documents for this domain. Up to {{limit}} files per upload, {{maxMb}} MB each.",
      addFiles: "Add PDF file(s)",
      uploading: "Uploading…",
      uploadSucceeded: "Documents uploaded.",
      empty: "No documents have been added to this domain yet.",
      table: {
        fileName: "File name",
        embeddingStatus: "Sync",
        addedAt: "Added",
        lastModifiedAt: "Last modified",
        actions: "Actions",
        ariaLabel: "Domain documents",
      },
      chatSearch: {
        label: "Use these documents for chat",
        help:
          "When on, chat answers this domain's questions from these documents alongside the " +
          "structured data. When off, only the structured data is used and the documents are " +
          "never searched.",
      },
      embedding: {
        action: "Sync Documents",
        running: "Syncing…",
        started: "Sync started. This can take a few minutes.",
        completed: "Document sync finished.",
        completedWithFailures: "Document sync finished — some files failed.",
        notEmbedded: "Not synced",
      },
      deleteAria: "Delete {{filename}}",
      deleteDialog: {
        heading: "Delete document?",
        subheading:
          "{{filename}} will be permanently removed from storage. This cannot be undone.",
        cancel: "Cancel",
        confirm: "Delete Document",
      },
      errors: {
        someFilesFailed: "These files were not uploaded:",
        fileLine: "{{filename}} — {{message}}",
        notPdf: "Only PDF files are supported.",
        tooLarge: "The file is larger than {{maxMb}} MB.",
        tooManyFiles:
          "You selected {{count}} files. Add at most {{limit}} files at a time.",
      },
    },
    alation: {
      tab: "Alation",
      title: "Alation catalog",
      subtitle: "Connect an Alation service account to reach your catalog.",
      connected: "Connected",
      connectedTo: "{{host}} · client {{clientId}}",
      loading: "Loading Alation configuration",
      lastVerified: "Credentials last verified {{when}}.",
      neverVerified: "These credentials have not been verified yet.",
      sync: {
        never:
          "This domain has not been synced with Alation yet. Start one from the Enrichment tab's " +
          "Model actions menu.",
        running: "Syncing with Alation…",
        failed: "The last Alation sync did not finish.",
        lastRun: "Last synced {{when}}",
        lastRunUnknown: "Last sync",
        counts:
          "Matched {{tables}} tables · created {{entitiesCreated}} entities · " +
          "updated {{entities}} entities and {{fields}} fields · {{terms}} glossary terms.",
        modelCounts:
          "Added {{relationships}} relationships · {{dimensions}} dimensions · " +
          "{{measures}} measures · {{ontologies}} ontologies · {{rules}} rules · " +
          "{{synonyms}} synonyms · {{examples}} examples.",
        proposals_one: "{{count}} change needs review in the Enrichment tab.",
        proposals_other: "{{count}} changes need review in the Enrichment tab.",
        unmatched_one:
          "{{count}} selected table is not in the Alation catalog: {{tables}}.",
        unmatched_other:
          "{{count}} selected tables are not in the Alation catalog: {{tables}}.",
      },
      empty: {
        title: "Alation is not connected",
        body:
          "Add an Alation OAuth client id and client secret. We verify them against your Alation " +
          "instance before storing them, encrypted.",
        readOnlyBody:
          "An administrator has not connected an Alation account for this domain yet.",
      },
      actions: {
        config: "Connect Alation",
        update: "Update connection",
      },
      chat: {
        title: "Ask the catalog",
        subtitle: "Questions are answered live from Alation. Nothing is saved.",
        welcome:
          "Ask me about anything in your Alation catalog \u2014 what a table holds, what a "
          + "column means, who owns it, or how the business defines a term.",
        inputPlaceholder: "Ask about a table, column or glossary term\u2026",
        working: "Searching the catalog\u2026",
        send: "Send",
        stopAria: "Stop answering",
        newChat: "Start a new conversation",
        suggestions: {
          tables: "Which tables hold customer data?",
          glossary: "What does our glossary say about active users?",
          owner: "Who owns the orders table?",
        },
        errors: {
          turnFailed: "Could not get an answer from Alation. Please try again.",
        },
      },
      config: {
        title: "Connect to Alation",
        subtitle:
          "These credentials come from an Alation OAuth service account. Only an Alation admin " +
          "can create one.",
        closeAria: "Close Alation configuration",
        fields: {
          baseUrl: "Alation host",
          clientId: "Client ID",
          clientSecret: "Client secret",
        },
        placeholders: { baseUrl: "your-company.alationcloud.com" },
        hints: {
          baseUrl: "The host of your Alation instance. A full URL works too.",
          clientSecret:
            "Stored encrypted. It is never shown again after saving.",
        },
        validation: { required: "This field is required." },
        testSucceeded:
          "Connected to {{server}}. {{count}} catalog tools are available.",
        actions: {
          cancel: "Cancel",
          test: "Test connection",
          connect: "Connect",
        },
        errors: {
          testFailed: "Could not connect to Alation with these credentials.",
          saveFailed: "Could not save the Alation configuration.",
        },
      },
    },
    access: {
      tab: "Access",
      empty: "No one has been granted a role on this domain yet.",
      addAccess: "Add Access",
      // Says why the actions are gone. Without it the tab reads as a permissions bug, since the
      // rule is deliberately not a permission -- not even an Administrator can edit here.
      ssoManaged:
        "Access to this domain is managed by your SSO role assignment rules. Everyone signs in through your identity provider, so roles are granted and revoked automatically from the attribute you mapped. Change the rules in Settings \u2192 SSO.",
      table: {
        user: "User",
        roles: "Roles",
        rls: "Row-level security",
        ariaLabel: "Domain access grants",
      },
      // Row-level security: the column that restricts a domain-level user's rows, and each user's
      // values. Only Super Administrators and tenant-role holders see or change these settings.
      rls: {
        title: "Row-level security",
        description:
          "Limit what users with a domain role can see to the rows that match the values you assign them. Every query they run on this domain is filtered by the column below, on top of any filter the query already has. Super Administrators and users with a tenant role always see every row.",
        column: "Row-level security column",
        columnHelp:
          "Choose a published column to turn row-level security on, or clear it to turn it off. Set each user's values in the table below.",
        noColumns: "No published columns",
        save: "Save",
        saveFailed: "Failed to save row-level security. Please try again.",
        exempt: "Exempt",
        exemptHelp: "Super Administrators and users with a tenant role see every row.",
        valueChip: "{{key}}: {{values}}",
        missingBlocked: "No values for {{key}} \u2014 their queries are blocked",
        editAria: "Edit row-level security values for {{user}}",
        activeNotice:
          "Row-level security is on for this domain: users with a domain role only see rows matching their assigned {{key}} values.",
        valuesDialog: {
          title: "Row-level security for {{user}}",
          closeAria: "Close dialog",
          description:
            "This user only sees rows whose values are listed here. Leave it empty and every query they run on this domain is blocked.",
          fieldHelp: "Values of {{column}}. Type a value and press Enter, or paste a comma-separated list.",
          cancel: "Cancel",
          save: "Save Values",
          saveFailed: "Failed to save the values. Please try again.",
        },
      },
      // Each role chip carries its own grant date, since a user's roles were granted at different
      // times and one row can no longer show a single "Granted" column.
      grantedOn: "Granted {{date}}",
      deleteAria: "Remove {{role}} access for {{user}}",
      deleteDialog: {
        heading: "Remove access?",
        subheading:
          "{{user}} will lose the {{role}} role on this domain. You can grant it again later.",
        cancel: "Cancel",
        confirm: "Remove Access",
      },
      errors: {
        deleteFailed: "Failed to remove access. Please try again.",
      },
      newAccessModal: {
        title: "Add Access",
        closeAria: "Close dialog",
        fields: {
          user: "User",
          role: "Role",
        },
        validation: {
          required: "This field is required.",
        },
        actions: {
          cancel: "Cancel",
          addAccess: "Add Access",
        },
        errors: {
          saveFailed: "Failed to add access. Please try again.",
        },
      },
    },
    generationInstructions: {
      action: "Generation instructions",
      actionWithContent: "Generation instructions ({{count}} characters)",
      title: "Semantic model generation instructions",
      close: "Close dialog",
      help: "Markdown describing this domain for the AI: what the tables mean, the grain a view is at, naming conventions, and any rule the column names do not reveal. It is read on the next enrichment run and treated as authoritative where it contradicts what the AI would otherwise infer.",
      field: "Instructions (Markdown)",
      upload: "Upload .md file",
      counter: "{{count}} of {{max}} characters",
      tooLong: "Instructions must be {{max}} characters or fewer.",
      cancel: "Cancel",
      save: "Save instructions",
      saveFailed: "Could not save generation instructions. Please try again.",
      saveSuccess: "Generation instructions saved.",
    },
    canvas: {
      tab: "Canvas",
      tabsAria: "Domain workspace sections",
      empty: "Select and add the tables from the library to build your canvas",
      addSelected: "ADD TO CANVAS",
      updateSelected: "UPDATE THE CANVAS",
      saveSuccess: "Updated successfully",
      deleteSuccess: "Deleted successfully",
      saving: "Saving…",
      dropAria: "Canvas drop area",
      arrange: "Arrange by relationships",
      deleteTableAria: "Remove {{table}} from canvas",
      deleteColumnAria: "Remove {{column}} from {{table}}",
      collapseTableAria: "Collapse {{table}}",
      expandTableAria: "Expand {{table}}",
      showAllColumns: "Show all {{count}} columns",
      showFewerColumns: "Show fewer columns",
      markKeyAria: "Mark {{column}} as part of this table's key",
      unmarkKeyAria: "Remove {{column}} from this table's key",
      compositeKey: "Key: {{columns}}",
      compositeKeyTooltip:
        "These columns together identify one row. Enrichment reads this as the table's key.",
      pk: "PK",
      fk: "FK",
      part: "PART",
      markPartitionAria:
        "Declare {{column}} a partition key of this table, and how it stores a date",
      partitionDeclared: "Partition key of this table \u2014 {{format}}",
      partitionNoDateKey: "Partition key, not a date",
      unmarkPartition: "Not a partition key",
      relationships: {
        willCreateEntities_one:
          "{{tables}} is not in the semantic model yet. Saving adds it, using the columns on the canvas. Enrichment will describe it properly on the next run.",
        willCreateEntities_other:
          "{{tables}} are not in the semantic model yet. Saving adds them, using the columns on the canvas. Enrichment will describe them properly on the next run.",
        connectDuplicate:
          "These two columns are already joined by a relationship.",
        connectInvalid:
          "Drag from one column to another column to create a relationship.",
        editEdgeAria: "Edit relationship {{name}}",
        deleteEdgeAria: "Delete relationship {{name}}",
      },
    },
    semanticModel: {
      tab: "Enrichment",
      empty: "Publish your domain to generate a semantic model.",
      pending:
        "Semantic model generation is queued. This page will update automatically.",
      running: "Generating semantic model…",
      progress: {
        hint: "This page updates automatically while enrichment runs.",
        percent: "{{value}}% complete",
        stages: {
          queued: "Queued for enrichment",
          fetching_metadata: "Fetching warehouse metadata",
          sampling_tables: "Sampling table rows",
          discovering_semantics: "Discovering semantic meaning",
          persisting_and_embedding: "Saving model and building search indexes",
        },
      },
      failedTitle: "Generation failed",
      failedHintReverted:
        "The domain has been moved back to draft so you can adjust the workspace and publish again.",
      failedHintPublished:
        "Your published tables are unchanged. Retry enrichment after fixing any issues below.",
      retryEnrichment: "Retry enrichment",
      retryPublish: "Publish again",
      skippedTitle: "Generation not run",
      readiness: {
        title: "Domain AI Readiness",
        overallScore: "Overall score: {{score}}%",
        statusEnriched: "Enriched",
        statusNotEnriched: "Not enriched",
        lastEnriched: "Last enriched: {{date}}",
        lastEnrichedNever: "Last enriched: never",
        entitiesDefined: "Entities Defined",
        relationshipsOriented: "Relationships Oriented",
        needsInput: "Needs Input",
        pendingProposals: "Pending Proposals",
        readyChip: "{{score}}% Ready",
        rerun: "Re-run AI Enrichment",
        rerunWithAnswers: "Save Answers & Re-run Enrichment",
      },
      graph: {
        viewButton: "View model graph",
        overlayTitle: "Semantic model graph",
        overlayClose: "Close graph",
        empty: "No entities to display in the graph yet.",
        legendFactEntity: "Fact entity",
        legendDimensionEntity: "Dimension entity",
        legendRelationship: "Relationship",
        legendRule: "Business rule",
        legendMeasure: "Measure",
        legendOntology: "Ontology",
        legendGlossary: "Glossary term",
        legendDimension: "Dimension",
        legendMetric: "Metric",
        canvasHint:
          "Force-directed layout. Pan and zoom to explore. Click a node to view its details.",
        detailsHint:
          "Click a node on the graph to view its metadata, fields, and assumptions.",
        detailsDescription: "Description",
        detailsMetadata: "Metadata",
        detailsRelationships: "Relationships",
        nodeKindEntity: "Entity",
        needsClarification: "Needs clarification",
      },
      rerun: {
        queued: "Enrichment re-run queued. Updates will appear automatically.",
        failed: "Could not re-run enrichment. Please try again.",
        menuButton: "Model actions",
        incrementalHelp:
          "Re-run enrichment for the tables that changed and the questions you answered. Items you have approved stay put.",
        fullAction: "Full refresh",
        fullHelp:
          "Rebuild every table from scratch. Slower, and items you have approved may come back for review.",
      },
      alationSync: {
        action: "Sync with Alation",
        hint:
          "Pull table, column and glossary metadata from your Alation catalog into this model. " +
          "Anything you have approved stays put; where Alation disagrees, its version is staged " +
          "for review.",
        running: "A sync is already running.",
        queued: "Alation sync started. This can take a few minutes.",
        failed: "Could not start the Alation sync. Please try again.",
        succeeded:
          "Alation sync finished. See the Alation tab for what changed.",
        runFailed:
          "The Alation sync did not finish. See the Alation tab for details.",
      },
      syncIndexes: {
        action: "Update search & graph",
        hint: "After manual edits, rebuild embeddings and the relationship graph from the saved model. This does not re-run AI enrichment.",
        succeeded: "Search and graph indexes updated.",
        failed: "Could not update search indexes. Please try again.",
      },
      nav: {
        aria: "Semantic model sections",
        jumpAria:
          "Go to {{section}}: {{count}} items, {{needsReview}} need review",
      },
      sections: {
        overview: "Overview",
        entityDefinitions: "Entity Definitions",
        relationshipSemantics: "Relationship Semantics",
        businessGlossary: "Business Glossary",
        queryPatterns: "Query Patterns",
        measureDefinitions: "Measures",
        metricDefinitions: "Metrics",
        ontologies: "Ontologies",
        dimensionDefinitions: "Dimension Definitions",
        rules: "Business rules",
        candidates: "Candidates",
        proposals: "Pending Proposals",
        synonyms: "Synonyms",
        clarifications: "Clarifications",
        clarificationsOpen: "Open Clarifications",
        clarificationsAnswered: "Previously Answered",
        rationale: "Generation rationale",
        assumptions: "Assumptions",
        answerInChat: "Answer in chat",
        answerInChatCurrent: "Current question in chat",
        assumptionNoQuestion:
          "No interview question yet — add context in clarification chat.",
        validationErrors: "Validation issues",
      },
      queryPatterns: {
        askInChat: "Ask in chat: {{question}}",
      },
      clarifications: {
        title: "Clarifications",
        subtitle:
          "Answer open questions and re-run enrichment to refine the semantic model. Previously answered questions can be edited.",
        openHeading: "Open Clarifications",
        openHeadingCount: "{{count}} unanswered",
        answeredHeading: "Previously Answered",
        answeredHeadingCount: "{{count}} answered",
        noOpen:
          "No open clarifications. All previously generated questions have been answered.",
        noClarifications:
          "No clarification questions were raised by the AI. You can still re-run enrichment to refresh the model.",
        answerPlaceholder: "Type your answer here…",
        answerLabel: "Your answer",
        rationaleLabel: "Why we are asking",
        unsavedNote: "You have unsaved answers.",
      },
      chat: {
        title: "Semantic Model Chat",
        subtitle:
          "Ask questions about the current model or share corrections and context. Structured questions appear when needed — use Apply & Re-generate to refresh the model.",
        emptyTranscript:
          "No conversation yet. Ask about the model or share context to get started.",
        autoResolvedNote: "Auto-resolved by an earlier answer: {{reason}}",
        autoResolvedChip: "Auto-resolved",
        skipped: "Skipped by user",
        inputPlaceholder:
          "Ask about the model or share context for regeneration…",
        send: "Send",
        sending: "Sending…",
        skipQuestion: "Skip this question",
        applyRefine: "Apply & Re-generate model",
        applyRefineDisabled: "Apply & Re-generate (publish the domain first)",
        openCount: "{{count}} open",
        openCountSingle: "1 open",
        needsInputCount: "{{count}} need input",
        needsInputCountSingle: "1 needs input",
        noOpenQuestionsNeedsInput:
          "No structured questions are queued, but {{count}} model items still need input. Ask in chat or use Apply & Re-generate.",
        resolvedCount: "{{count}} resolved",
        progressLabel: "{{resolved}} of {{total}} resolved",
        remainingItemsTitle: "Still needs input",
        remainingItemsCountPlural: "({{count}} items)",
        remainingItemsCountSingular: "(1 item)",
        askAboutItem: "Ask me →",
        reviewSkippedButton: "Review skipped",
        reviewSkippedHint_one: "1 question was skipped — tap to re-ask it.",
        reviewSkippedHint_other:
          "{{count}} questions were skipped — tap to re-ask them.",
        focusedTargetChip: "Describing: {{label}}",
        clearFocusedTarget: "Clear",
        notReadyTitle: "Chat not available yet",
        notReadyHint:
          "Publish the domain and let the first enrichment finish before starting a clarification chat.",
        sendFailed: "Something went wrong. Please try again.",
        openFabAria: "Open semantic model chat",
        closeFabAria: "Close semantic model chat",
      },
      search: {
        placeholder: "Search {{section}}",
        clearAria: "Clear search",
        noMatches: 'No {{section}} match "{{query}}".',
        labels: {
          entities: "entities",
          relationships: "relationships",
          measures: "measures",
          metrics: "metrics",
          dimensions: "dimensions",
          rules: "business rules",
          ontologies: "ontologies",
          candidates: "candidates",
          glossary: "glossary terms",
          queryPatterns: "query patterns",
          synonyms: "synonyms",
        },
      },
      empty_section: {
        entities:
          "No entity definitions yet. Run AI enrichment to generate them, or add one manually.",
        relationships:
          "No relationships were detected between the published tables. Add one manually to define how entities connect.",
        glossary:
          "No business glossary terms yet. Run AI enrichment to generate them, or add one manually.",
        queryPatterns: "No example query patterns were generated.",
        measures:
          "No measures yet. Run AI enrichment to generate them, or add one manually.",
        metrics:
          "No metrics defined yet. A metric combines one or more measures — possibly across tables — into a named analytical concept, e.g. Return Rate = Returns Amount ÷ Orders Amount.",
        ontologies:
          "No ontologies yet. Run AI enrichment to discover classification hierarchies, or add one manually.",
        dimensions:
          "No dimension definitions yet. Run AI enrichment to generate them, or add one manually.",
        rules:
          "No business rules yet. Run AI enrichment to generate them, or add one manually.",
        candidates:
          "No candidates yet. Candidate generation runs automatically after enrichment, or add one manually.",
        proposals:
          "No pending proposals. Regeneration hasn't suggested any changes to review yet.",
        semanticMeaning: "No semantic meaning defined",
      },
      labels: {
        grain: "Grain",
        primaryTable: "Primary table",
        primaryTableNone: "None",
        primaryTableUnknown:
          "This table is not in the domain. Add it to the canvas, or pick another.",
        dimensions: "Dimensions",
        primaryKeys: "Primary keys",
        backingSources: "Backing sources",
        expression: "Expression",
        expressionHelp:
          "The complete expression, aggregate included \u2014 SUM(amount), COUNT(*), or a CASE WHEN.",
        aggregation: "Aggregation",
        sourceTable: "Source table",
        joinCondition: "Condition",
        joinType: "Type",
        physicalJoin: "Join",
        cardinalityFallback: "relation",
        confidence: "Confidence",
        provenance: "Source",
        sourceBinding: "Source binding",
        fieldBinding: "{{table}}.{{column}}",
        fieldBindingTableOnly: "{{table}}",
      },
      fieldHelp: {
        openAria: "What is {{field}}?",
        impactHeading: "If you change it",
        name: {
          label: "Name",
          what: "The business name for this. People use it when they ask questions in chat, and it is what shows up on charts and cards.",
          impact:
            "Asking by the old name may stop working. Saved questions, charts and alerts that referred to it can go quiet until they are pointed at the new name.",
        },
        description: {
          label: "Description",
          what: "A plain sentence explaining what this means to the business, and anything unusual about it.",
          impact:
            "This is how Olive decides which item answers a question. A vague or wrong description is the most common reason the wrong number comes back, so it is worth being specific.",
        },
        entity_type: {
          label: "Entity type",
          what: "Whether this describes things that happen (orders, cases, messages) or things that simply exist (customers, products, agents).",
          impact:
            "Events are counted and trended over time; things are used to group and filter. Getting it the wrong way round makes trends and breakdowns look odd.",
        },
        grain: {
          label: "Grain",
          what: 'What one row means, in plain words \u2014 "one row per order", or "one row per case per day".',
          impact:
            "This is how Olive knows whether counting rows counts the real thing. If the grain is wrong, totals can silently double-count.",
        },
        primary_table: {
          label: "Source table",
          what: "The warehouse table this is read from.",
          impact:
            "Point it somewhere else and every number for this item comes from the new table. If that table does not hold the same rows, answers change without any other edit.",
        },
        partition_columns: {
          label: "Partitioned / clustered by",
          what: "The columns the warehouse physically organizes this table by \u2014 typically a date key such as DATE_ID on a large fact or snapshot table.",
          impact:
            "A query filtering dates through a date dimension does not prune this table on its own. Naming the partition column lets the generator repeat the same window on it, so the warehouse skips partitions instead of reading everything and discarding rows afterwards.",
        },
        date_key_format: {
          label: "Date key format",
          what: "How this column stores a date when it is not a real DATE column \u2014 a DATE_ID of 20260801 is the YYYYMMDD number format.",
          impact:
            "Only a declared format is used. Leave it unset and the column is left out of the pruning filter entirely; set it wrongly and queries filter on the wrong window.",
        },
        primary_key: {
          label: "Primary key",
          what: "The field or fields that identify one row uniquely \u2014 an order number, or a case id.",
          impact:
            "Used to tell whether joining to other data is safe. A wrong key lets a join multiply rows, which inflates totals.",
        },
        backing_sources: {
          label: "Backing sources",
          what: "Every warehouse table this entity draws from.",
          impact:
            "Informational. Removing a table here does not stop it being used \u2014 the bindings on each field decide that.",
        },
        data_type: {
          label: "Data type",
          what: "The kind of value in this column: text, a number, a date.",
          impact:
            "Decides how the value can be used \u2014 numbers get totalled, dates drive time filters and trends. A date recorded as text cannot be trended.",
        },
        source_column: {
          label: "Source column",
          what: "The warehouse column this field reads from.",
          impact:
            "Everything about this field comes from the column you pick. Choosing the wrong one gives confidently wrong answers, because nothing else looks different.",
        },
        source_ref: {
          label: "Source binding",
          what: "The exact table and column in the warehouse behind this item.",
          impact:
            "This is the physical wiring. Editing it repoints the item at different data; if the table or column does not exist, queries using it fail instead of returning a wrong number.",
        },
        expression: {
          label: "Calculation",
          what: "The complete calculation, including how values are combined \u2014 for example SUM(amount), COUNT(*), or a count of only the rows that match a condition.",
          impact:
            "This is the number itself. Change it and every chart, answer and alert using this measure changes with it. Leaving out how values combine gives one row's value instead of a total.",
        },
        source_table: {
          label: "Source table",
          what: "The warehouse table this measure is calculated from.",
          impact:
            "Repoints the calculation at a different table. Numbers change, and if the column in the calculation is not in the new table, the measure stops working.",
        },
        entity: {
          label: "Belongs to",
          what: "Which entity this belongs to \u2014 the thing it describes, like orders or cases.",
          impact:
            "Decides what it can be broken down by and which other data it can be combined with. Moving it to the wrong entity makes valid breakdowns disappear from answers.",
        },
        filters: {
          label: "Always-on filters",
          what: "Conditions that are always applied, one per line \u2014 for example only completed orders.",
          impact:
            "These apply every single time, everywhere. Adding one makes the number smaller for everyone; removing one makes it bigger. It is the quietest way for a number to shift.",
        },
        combination_strategy: {
          label: "How it combines",
          what: "How the measures below are put together: passed straight through, divided one by another, or subtracted.",
          impact:
            "Changes the arithmetic. The order of the measures matters for divide and subtract \u2014 the first is the top or the starting figure.",
        },
        component_measures: {
          label: "Built from",
          what: "The measures this is assembled from, in order. For a division the first is the top; for a subtraction the first is what is subtracted from.",
          impact:
            "Reordering them changes the result \u2014 a rate becomes its own upside down. Removing one can leave the metric unable to run at all.",
        },
        metric_type: {
          label: "Metric type",
          what: "A loose label for the kind of figure this is, such as a ratio or a derived number.",
          impact:
            "A hint only. It nudges how the figure is described in stories and does not change the number.",
        },
        executable: {
          label: "Can run",
          what: "Whether Olive found a safe way to combine this metric's measures across their tables.",
          impact:
            "Worked out automatically, not set by hand. When this is off, the metric is still listed but cannot return a number \u2014 usually a missing relationship between the tables.",
        },
        source_entity: {
          label: "From",
          what: "The entity this relationship starts at.",
          impact:
            "Together with the matching fields, this is what makes the two sets of data joinable. Wrong here and the join either returns nothing or the wrong rows.",
        },
        target_entity: {
          label: "To",
          what: "The entity this relationship connects to.",
          impact:
            "Together with the matching fields, this is what makes the two sets of data joinable. Wrong here and the join either returns nothing or the wrong rows.",
        },
        cardinality: {
          label: "How many match",
          what: "How many rows on each side pair up \u2014 one case to many messages, or one message to one case.",
          impact:
            'Tells Olive whether combining the two can duplicate rows. Declaring "one to one" when it is really "one to many" inflates every total that crosses this relationship.',
        },
        join_type: {
          label: "Join type",
          what: "Whether rows with no match on the other side are kept or dropped.",
          impact:
            "Dropping unmatched rows quietly shrinks totals \u2014 cases with no messages would disappear from a count of cases.",
        },
        join_ref: {
          label: "Matching fields",
          what: "The two columns whose values must be equal for rows to be treated as related.",
          impact:
            "The join is only as right as this pair. Matching on the wrong columns can return far too many rows, or none.",
        },
        safe_for_aggregation: {
          label: "Safe for totals",
          what: "Whether totals stay correct when data is combined through this relationship.",
          impact:
            "Turning this on when the sides do not pair up cleanly lets one row be counted many times, which inflates totals with no visible error.",
        },
        definition: {
          label: "Definition",
          what: "The rule itself \u2014 which field it applies to and what each stored value means in business terms.",
          impact:
            "Changes how coded values are read. If a status code is mapped to the wrong label, answers use the wrong words for the right rows, which is hard to spot.",
        },
        field: {
          label: "Applies to",
          what: "The column this rule governs.",
          impact:
            "Moving the rule to another column applies its meaning to different data, and stops it applying where it did before.",
        },
        source_field: {
          label: "Source column",
          what: "The column whose distinct values make up this list.",
          impact:
            "Repoints the list at another column. A column with thousands of values is not a business list \u2014 it makes queries slow and the list meaningless.",
        },
        nodes: {
          label: "Values",
          what: "The values in this list and how they group together.",
          impact:
            "These are what people can filter and group by. A missing value cannot be asked about; a wrongly grouped one lands in the wrong bucket.",
        },
        term: {
          label: "Term",
          what: "The business word or phrase being defined.",
          impact:
            "This is what people are expected to say. Changing it changes which wording Olive recognises in a question.",
        },
        unit_type: {
          label: "Unit type",
          what: "What kind of quantity this is \u2014 money, a percentage, a count, a duration, a ratio.",
          impact:
            "Decides how the value is displayed and whether adding it up makes sense. Percentages and ratios cannot be summed across rows, so labelling a count as a percentage stops totals being shown.",
        },
        unit_symbol: {
          label: "Unit symbol",
          what: "The symbol shown next to the value, such as % or a currency code.",
          impact:
            "Display only \u2014 it never changes the number. A wrong symbol makes a correct figure read as the wrong thing.",
        },
        decimal_places: {
          label: "Decimal places",
          what: "How many digits to show after the decimal point.",
          impact:
            "Display only. Rounding to zero places can make small but real movements look like no change at all.",
        },
        multiplier: {
          label: "Display multiplier",
          what: "A factor applied before showing the value \u2014 use 100 to show a stored 0.42 as 42%.",
          impact:
            "Display only, but easy to double up: if the calculation already returns a percentage, setting this to 100 shows a figure a hundred times too large.",
        },
        materiality_basis: {
          label: "What counts as a big move",
          what: "How Olive judges whether a change in this figure is worth telling you about: by amount, by percentage, by share of the total, or by leaving a normal range.",
          impact:
            "Controls which changes become stories. Too strict and real movements go unreported; too loose and small wobbles crowd out the ones that matter.",
        },
        min_abs_delta: {
          label: "Smallest change worth reporting",
          what: "How much this figure has to move, in its own units, before the move is worth a mention.",
          impact:
            "Raise it and small movements stop being reported. Set it far too high and this figure stops producing stories at all.",
        },
        min_level: {
          label: "Smallest figure worth reporting",
          what: "How large the figure itself has to be before any move in it is worth reporting.",
          impact:
            "Filters out noise from very small numbers, where a move of two looks like a doubling. Set too high and genuinely small but important segments go dark.",
        },
        min_percent_change: {
          label: "Smallest percentage change",
          what: "How far this figure has to move in percentage terms before the move is worth a mention.",
          impact:
            "Raise it and only larger swings are reported. On figures with tiny starting values, a low setting reports moves that are not really meaningful.",
        },
        min_share_of_total: {
          label: "Smallest share worth reporting",
          what: "How much of the overall total a segment has to account for before it is worth reporting on its own.",
          impact:
            "Keeps tiny slices out of the story. Set too high and real problems in smaller segments are never surfaced.",
        },
        normal_low: {
          label: "Bottom of normal",
          what: "The lowest value considered business-as-usual for this figure.",
          impact:
            "Below this, Olive treats the figure as out of its normal range and worth reporting. The bottom must be lower than the top or the range is ignored.",
        },
        normal_high: {
          label: "Top of normal",
          what: "The highest value considered business-as-usual for this figure.",
          impact:
            "Above this, Olive treats the figure as out of its normal range and worth reporting. The top must be higher than the bottom or the range is ignored.",
        },
        materiality_notes: {
          label: "Notes",
          what: "Free notes about how this figure should be judged, for whoever reviews it next.",
          impact:
            "Notes only \u2014 they change nothing about the numbers or which stories appear.",
        },
        mapping: {
          label: "Value meanings",
          what: "What each stored code means in business terms \u2014 for example 1 means Monthly and 2 means Annual.",
          impact:
            "Changes the words used for those rows in every answer. A code mapped to the wrong label is one of the hardest errors to notice, because the numbers stay right.",
        },
        filter_expression: {
          label: "Condition",
          what: "The condition that decides which rows this rule applies to.",
          impact:
            "Widening it applies the rule to rows it did not cover before; narrowing it silently leaves rows unlabelled.",
        },
        category: {
          label: "Category",
          what: "A grouping for this list, so related lists sit together.",
          impact:
            "Organisational only \u2014 it changes nothing about the values or the answers.",
        },
        is_ordered: {
          label: "Values have an order",
          what: "Whether these values run in a meaningful sequence, like Low, Medium, High, rather than being unordered labels.",
          impact:
            "Ordered lists are shown and sorted in their real sequence. Turn it off and they fall back to alphabetical, which puts High before Low.",
        },
        synonyms: {
          label: "Other ways to say it",
          what: "Alternative words people use for the same thing, one per line.",
          impact:
            "Each one is another phrasing Olive will recognise in a question. Removing one means questions using that wording may no longer be understood.",
        },
        join_from: {
          label: "Matching field on this side",
          what: "The column on the first entity whose values must match the other side.",
          impact:
            "This half of the pair decides which rows are treated as related. The wrong column can return far too many rows, or none.",
        },
        join_to: {
          label: "Matching field on the other side",
          what: "The column on the second entity whose values must match the first.",
          impact:
            "This half of the pair decides which rows are treated as related. The wrong column can return far too many rows, or none.",
        },
        metrics: {
          label: "Figures compared",
          what: "The figures this investigation looks at together.",
          impact:
            "Changes what is being compared, and so what the finding can possibly say. Order matters where the comparison divides or subtracts.",
        },
        reason_for_running: {
          label: "Why run this",
          what: "A note on why this comparison is worth making, for whoever reviews the findings.",
          impact:
            "Context only \u2014 it changes nothing about what is investigated or found.",
        },
        business_question: {
          label: "Business question",
          what: "The question in plain words that this line of investigation is meant to answer.",
          impact:
            "This is what Olive goes looking for. A sharper question produces a more useful finding; a vague one produces a vague one.",
        },
        relationship_type: {
          label: "Comparison type",
          what: "The kind of comparison being made between the figures \u2014 a ratio, a divergence, a funnel, a correlation.",
          impact:
            "Shapes how the finding is investigated and written up. It does not change the underlying numbers.",
        },
        formula: {
          label: "Formula",
          what: "How the figures are combined for this comparison, when it needs one.",
          impact:
            "Changes the derived number this investigation reports. It does not affect the measures it is built from.",
        },
        derived_name: {
          label: "Derived name",
          what: "What to call the combined figure when it appears in a finding.",
          impact:
            "Wording only \u2014 it changes how the finding reads, not what it found.",
        },
        dimensions: {
          label: "Break down by",
          what: "The attributes this investigation is allowed to slice the figures by, such as region or channel.",
          impact:
            "Limits where Olive looks. Removing one means problems hiding in that breakdown will not be found.",
        },
        priority_score: {
          label: "Priority",
          what: "How important this investigation is relative to the others, from 0 to 1.",
          impact:
            "Decides what gets looked at first when there is more to investigate than time allows. It does not change what any finding says.",
        },
        curation_status: {
          label: "Status",
          what: "Whether this has been approved to run, is still proposed, or has been dismissed.",
          impact:
            "Dismissed items are not investigated at all. Approving one puts it into the running order.",
        },
      },
      entities: {
        addAction: "Add entity",
        addFirst: "Add your first entity",
        addTitle: "Add entity",
        editTitle: "Edit entity",
        detailTitle: "Entity details",
        fieldsSection: "Fields",
        openDetailAria: "View details for {{name}}",
        editAria: "Edit {{name}}",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save entity",
        saveFailed: "Could not save entity changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete entity",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteConfirmationReferenced:
          'Are you sure you want to delete "{{name}}"? It is referenced by {{count}} other item(s) (relationships, rules, dimensions, or measures) — deleting it may leave those references broken. This action cannot be undone.',
        deleteFailed: "Could not delete entity. Please try again.",
        deleteSuccess: "Entity deleted successfully.",
        nameRequired: "Entity name is required.",
        duplicateKey: "An entity with this key already exists.",
        keyLabel: "Key",
        fields: {
          name: "Name",
          description: "Description",
          entityType: "Entity type",
          grain: "Grain",
          grainPlaceholder: "one row per order",
          grainHelp: "What a single row of this entity represents.",
          primaryKey: "Primary key",
          partitionColumns: "Partitioned / clustered by",
        },
        entityFields: {
          name: "Field name",
          description: "Field description",
          dataType: "Data type",
          sourceColumn: "Source column",
          dateKeyFormat: "Date key format",
          dateKeyFormatNone: "Not a date key",
          dateKeyFormats: {
            yyyymmdd_int: "YYYYMMDD number (20260801)",
            yyyymmdd_string: "YYYYMMDD text ('20260801')",
            yyyymm_int: "YYYYMM number (202608)",
            iso_date_string: "ISO date text ('2026-08-01')",
            native_date: "DATE / TIMESTAMP column",
          },
          sourceColumnPlaceholder: "Select a column",
          sourceColumnEmpty: "Add tables to the canvas to choose columns.",
          sourceColumnInvalid: "This column is not available in the domain.",
          toggleSchemaAria: "Toggle schema {{schema}}",
          toggleTableAria: "Toggle table {{table}}",
          addAction: "Add field",
          removeAria: "Remove field {{name}}",
          unnamed: "without a name",
          empty:
            "No fields yet. Add one to make this entity usable in queries.",
        },
      },
      relationships: {
        addAction: "Add relationship",
        addFirst: "Add your first relationship",
        addTitle: "Add relationship",
        editTitle: "Edit relationship",
        viewTitle: "Relationship details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save relationship",
        saveFailed: "Could not save relationship changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete relationship",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFailed: "Could not delete relationship. Please try again.",
        deleteSuccess: "Relationship deleted successfully.",
        nameRequired: "Relationship name is required.",
        entitiesRequired: "Select both source and target entities.",
        sameEntityError: "Source and target entities must be different.",
        duplicateKey: "A relationship with this key already exists.",
        keyLabel: "Key",
        fields: {
          name: "Name",
          sourceEntity: "From entity",
          targetEntity: "To entity",
          relationshipType: "Relationship type",
          cardinality: "Cardinality",
          cardinalityHelp:
            "Pick a value from the list — it decides whether aggregates may cross this join.",
          joinFrom: "Join from",
          joinTo: "Join to",
          joinType: "Join type",
          safeForAggregation: "Safe to aggregate across this join",
          safeForAggregationHelp:
            "Leave off unless the join is one-to-one or onto a primary key. SUM and COUNT across an unsafe join are rejected.",
          compositeJoinNotice:
            "This relationship uses a multi-column join. Those columns are kept as they are and cannot be edited here.",
          joinColumnPlaceholder: "Select a column",
          joinColumnEmpty: "Add tables to the canvas to choose columns.",
          joinColumnInvalid: "This column is not available in the domain.",
        },
      },
      glossary: {
        addAction: "Add term",
        addFirst: "Add your first glossary term",
        addTitle: "Add glossary term",
        editTitle: "Edit glossary term",
        viewTitle: "Glossary term details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save term",
        saveFailed: "Could not save glossary changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete glossary term",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFailed: "Could not delete glossary term. Please try again.",
        nameRequired: "Term name is required.",
        duplicateKey: "A glossary term with this key already exists.",
        keyLabel: "Key",
        synonymsLabel: "Also known as",
        relatedMeasuresLabel: "Related metrics",
        fields: {
          name: "Term",
          definition: "Definition",
          synonyms: "Synonyms",
          synonymsPlaceholder: "e.g. ARR, annual recurring revenue",
          relatedMeasures: "Related metrics",
          relatedMeasuresPlaceholder: "e.g. revenue, mrr",
        },
      },
      ontologies: {
        addAction: "Add ontology",
        addFirst: "Add your first ontology",
        addTitle: "Add ontology",
        editTitle: "Edit ontology",
        viewTitle: "Ontology details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save ontology",
        saveFailed: "Could not save ontology changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete ontology",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFailed: "Could not delete ontology. Please try again.",
        deleteSuccess: "Ontology deleted successfully.",
        nameRequired: "Ontology name is required.",
        duplicateKey: "An ontology with this key already exists.",
        keyLabel: "Key",
        orderedBadge: "Ordered",
        levelsBadge: "{{count}} levels",
        sqlBadge: "SQL",
        nodeCount: "{{count}} nodes",
        nodeCount_one: "1 node",
        fields: {
          name: "Name",
          description: "Description",
          category: "Category",
          categoryNone: "None",
          sourceField: "Source field",
          sourceColumn: "Source column",
          entity: "Owning entity",
          entityNone: "None",
          nodes: "Nodes",
          nodesPlaceholder: "One node per line, e.g.\nBronze\nSilver\nGold",
          pathsPlaceholder:
            "One combination per line, levels separated by >, e.g.\nEurope > France > Paris\nEurope > Germany > Berlin",
          pathsHelp:
            "Leave a level empty to skip it, e.g. Europe >  > Paris.",
          isOrdered: "Ordered hierarchy",
        },
        hierarchy: {
          toggle: "Multiple columns (hierarchy)",
          toggleHelp:
            "Link several columns in order, e.g. region > country > city. Filters on these columns narrow each other.",
          levelsTitle: "Levels",
          levelName: "Level {{index}} name",
          levelColumn: "Level {{index}} column",
          addLevel: "Add level",
          removeLevelAria: "Remove level {{index}}",
          moveUpAria: "Move level {{index}} up",
          moveDownAria: "Move level {{index}} down",
        },
        values: {
          title: "Values",
          sourceManual: "Enter manually",
          sourceSql: "From a SQL query",
          sqlLabel: "Values query",
          sqlHelp:
            "Its columns fill the levels in order: the first column is level 1, the second level 2, and so on.",
          sqlHelpFlat: "The first column becomes the values.",
          useSuggestedQuery: "Use SELECT DISTINCT on the chosen columns",
          preview: "Preview",
          rerunPreview: "Run again",
          previewFailed: "Could not run the query.",
          previewCount: "{{count}} rows",
          previewTruncated: "Showing {{shown}} of {{total}} rows",
          previewEmpty: "The query returned no rows.",
          scheduleTitle: "Refresh schedule",
          scheduleHelp:
            "The query runs when you save, on demand, and on this schedule. Without a schedule it runs only when you save or refresh.",
          refreshNow: "Refresh now",
          saveToRefresh:
            "You changed the query or its columns. Save to run the new query; Refresh now runs the saved one.",
          refreshFailed: "Could not refresh the values. Please try again.",
          lastRefreshed: "Last refreshed {{when}}: {{count}} rows",
          lastRefreshFailed: "Last refresh failed {{when}}: {{error}}",
          neverRefreshed: "Not refreshed yet. The query runs when you save.",
          nodesFromQuery:
            "Filled by the query each time it runs, so they can't be edited here. Switch to Enter manually to type them instead.",
          noValuesYet: "No values yet. The query runs when you save.",
        },
        errors: {
          levelsIncomplete: "Pick a column for every level.",
          levelsDuplicate: "Each level must use a different column.",
          sqlRequired: "Enter the values query.",
          scheduleInvalid: "Enter a valid schedule.",
        },
      },
      measurement: {
        sectionTitle: "Measurement",
        unitType: "Unit type",
        unitTypeHelp:
          "How this value is measured. Pick one or type your own — the vocabulary is open.",
        unitSymbol: "Unit symbol",
        decimalPlaces: "Decimal places",
        multiplier: "Display multiplier",
        materialityBasis: "Material when",
        basisOptions: {
          auto: "Not set — decide automatically",
          absolute: "It moves by an absolute amount",
          percent: "It moves by a percentage",
          share: "It is a large enough share of the total",
          range: "It leaves a normal range",
        },
        minAbsDelta: "Minimum change",
        minLevel: "Minimum level",
        // Percentage points, not a fraction -- the gate compares this against a
        // percent_delta already multiplied by 100, unlike the share field below.
        minPercentChange: "Minimum change in percent (10 = 10%)",
        minShareOfTotal: "Minimum share of total (0.1 = 10%)",
        normalLow: "Normal range low",
        normalHigh: "Normal range high",
        rangeInverted: "The low bound must not be above the high bound.",
        notes: "Notes",
        autoSummary:
          "Not set — materiality is judged relative to this metric's own scale.",
        summaryLabel: "Materiality",
      },
      measures: {
        addAction: "Add measure",
        addFirst: "Add your first measure",
        addTitle: "Add measure",
        editTitle: "Edit measure",
        viewTitle: "Measure details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save measure",
        saveFailed: "Could not save measure changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete measure",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteConfirmationReferenced:
          'Are you sure you want to delete "{{name}}"? It is referenced by {{count}} metric(s) — deleting it may leave those metrics broken. This action cannot be undone.',
        deleteFailed: "Could not delete measure. Please try again.",
        deleteSuccess: "Measure deleted successfully.",
        nameRequired: "Measure name is required.",
        duplicateKey: "A measure with this key already exists.",
        keyLabel: "Key",
        noEntity: "None",
        fields: {
          name: "Name",
          description: "Description",
          entity: "Entity",
          filters: "Filters",
          filtersPlaceholder: "status = 'completed'",
          filtersHelp: "One SQL predicate per line. All of them are applied.",
        },
      },
      metrics: {
        addAction: "Add metric",
        addFirst: "Add your first metric",
        addTitle: "Add metric",
        editTitle: "Edit metric",
        viewTitle: "Metric details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save metric",
        saveFailed: "Could not save metric changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete metric",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFailed: "Could not delete metric. Please try again.",
        deleteSuccess: "Metric deleted successfully.",
        nameRequired: "Metric name is required.",
        duplicateKey: "A metric with this key already exists.",
        keyLabel: "Key",
        componentsRequired: "Select at least one component measure.",
        executable: "Executable",
        notExecutable: "Not executable",
        notExecutableReason: "Not executable: {{reason}}",
        fields: {
          name: "Name",
          description: "Description",
          combinationStrategy: "Combination",
          componentMeasures: "Component measures",
          metricType: "Metric type",
          grain: "Grain",
        },
        combinationStrategyOptions: {
          passthrough: "Passthrough (single measure)",
          ratio: "Ratio (A ÷ B)",
          difference: "Difference (A − B)",
          sum: "Sum (A + B + …)",
          custom: "Custom",
        },
      },
      dimensions: {
        addAction: "Add dimension",
        addFirst: "Add your first dimension",
        addTitle: "Add dimension",
        editTitle: "Edit dimension",
        viewTitle: "Dimension details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        saveFailed: "Could not save dimension changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete dimension",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteSuccess: "Dimension deleted successfully.",
        deleteFailed: "Could not delete dimension. Please try again.",
        fields: {
          name: "Name",
          description: "Description",
          isTimeDimension: "This is a time dimension",
          isTimeDimensionHelp:
            "Marks this as the date axis. Trends and period-over-period comparisons need it.",
          timeGrainSupport: "Supported grains",
        },
        timeGrainOptions: {
          day: "Day",
          week: "Week",
          month: "Month",
          quarter: "Quarter",
        },
      },
      rules: {
        addAction: "Add rule",
        addFirst: "Add your first business rule",
        addTitle: "Add business rule",
        editTitle: "Edit business rule",
        viewTitle: "Business rule details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save rule",
        saveFailed: "Could not save business rule changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete business rule",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFailed: "Could not delete business rule. Please try again.",
        nameRequired: "Rule name is required.",
        duplicateKey: "A rule with this key already exists.",
        keyLabel: "Key",
        noEntity: "None",
        mappingHint: "One mapping per line, e.g. A=active",
        filterExpressionHint:
          "Optional SQL predicate applied to queries. Leave blank to derive from code mappings on save.",
        fields: {
          name: "Name",
          description: "Description",
          field: "Field",
          mapping: "Code mappings",
          filterExpression: "Filter expression",
          entity: "Entity",
        },
      },
      proposals: {
        acceptAction: "Accept",
        rejectAction: "Dismiss",
        acceptAria: "Accept proposal for {{name}}",
        rejectAria: "Dismiss proposal for {{name}}",
        acceptSucceeded: "Proposal accepted and applied to the model.",
        acceptFailed: "Could not accept the proposal. Please try again.",
        rejectSucceeded: "Proposal dismissed.",
        rejectFailed: "Could not dismiss the proposal. Please try again.",
        targetTypes: {
          entity: "Entity",
          relationship: "Relationship",
          rule: "Rule",
          measure: "Measure",
          dimension: "Dimension",
          metric: "Metric",
          ontology: "Ontology",
          synonym: "Synonym",
          glossary: "Glossary",
          measure_format: "Measure format",
          dimension_format: "Dimension format",
          metric_format: "Metric format",
          measure_materiality: "Measure materiality",
          metric_materiality: "Metric materiality",
          candidate: "Candidate",
        },
      },
      review: {
        approve: "Approve",
        reject: "Reject",
        approveAria: "Approve {{name}}",
        rejectAria: "Reject {{name}}",
        approveAll: "Approve all",
        rejectAll: "Reject all",
        approvedBadge: "Approved",
        unreviewedBadge: "Needs review",
        approveFailed: "Could not approve this item. Please try again.",
        rejectFailed: "Could not reject this item. Please try again.",
        approveSectionFailed:
          "Could not approve this section. Please try again.",
        rejectSectionFailed: "Could not reject this section. Please try again.",
        conflictTitle: "Regeneration proposed a change to this approved item",
        conflictCurrent: "Current (approved)",
        conflictProposed: "Proposed",
        keepCurrent: "Keep current",
        useProposed: "Use proposed",
        startReview: "Review",
        startReviewAll: "Review all",
        startReviewAria: "Review {{count}} items one at a time",
        overlayTitle: "Review semantic model",
        closeAria: "Close review",
        decidedProgress: "{{decided}} of {{total}} decided",
        viewingPosition: "Viewing item {{position}}",
        queueEmpty: "Nothing left to review.",
        sampleRowsFrom: "Sample rows from {{table}}",
        highlightedColumn: "Highlighted column: {{column}}",
        highlightedColumnTyped: "Highlighted column: {{column}} ({{type}})",
        samplesFailed: "Couldn't load sample rows from {{table}}.",
        samplesEmpty: "{{table}} returned no rows.",
        samplesTableNotInDomain:
          "{{table}} isn't one of this domain's tables, so there are no sample rows to show.",
        proposedHeadingNew: "Proposed",
        proposedHeadingChanged: "Proposed change",
        back: "Back",
        skip: "Next",
        shortcutsAria: "Keyboard shortcuts",
        applyDecisions: "Apply {{count}} decisions",
        approveEdited: "Approve edited",
        editedBadge: "Edited",
        structureHint:
          "Edit these from the item's own card \u2014 they need the full editor.",
        batchFailed: "Could not apply your review decisions. Please try again.",
        discardTitle: "Discard your review?",
        discardBody:
          "{{count}} decisions have not been applied yet and will be lost.",
        discardConfirm: "Discard",
        discardCancel: "Keep reviewing",
        deleteTitle: "Delete {{count}} items?",
        deleteBody:
          "Rejecting these removes them from the semantic model, and that cannot be undone: {{names}}",
        deleteConfirm: "Apply and delete",
        deleteCancel: "Cancel",
        sections: {
          entity: "Entity",
          relationship: "Relationship",
          measure: "Measure",
          metric: "Metric",
          dimension: "Dimension",
          rule: "Rule",
          ontology: "Ontology",
          glossary: "Glossary term",
          synonym: "Synonym",
          example: "Query example",
          candidate: "Candidate",
          measure_format: "Measure format",
          dimension_format: "Dimension format",
          metric_format: "Metric format",
          measure_materiality: "Measure materiality",
          metric_materiality: "Metric materiality",
        },
        rejectAllConfirmTitle: "Reject {{count}} items in this section?",
        rejectModelConfirmTitle: "Reject {{count}} items across the whole model?",
        rejectAllConfirmBody:
          "Items nobody has approved yet are removed from the semantic model, and that cannot be undone. Approved items and items with a pending change keep their current value.",
        rejectAllConfirmConfirm: "Reject and remove",
        diffBefore: "Before",
        diffAfter: "After",
        valueYes: "Yes",
        valueNo: "No",
        queueHeading: "Items to review",
        groupProgress: "{{decided}}/{{total}}",
        summaryStep: "Summary",
        summaryTitle: "Ready to apply",
        summaryBody:
          "Nothing is saved until you apply. Click any item to go back to it.",
        summaryNothing:
          "You have not approved or rejected anything yet. Click an item on the left to review it.",
        summaryApproved: "Approved ({{count}})",
        summaryRejected: "Rejected ({{count}})",
        summarySkipped: "{{count}} skipped \u2014 they stay as they are.",
        summaryWillDelete: "Will be deleted",
        editedTag: "Edited",
        newItemBanner:
          "New \u2014 nothing approved to compare with, so everything below is being added.",
        changesOnly: "Changes only",
        splitCurrent: "Current",
        splitProposed: "Proposed",
        changedFieldCount: "{{count}} of {{total}} fields changed",
        unchangedEntriesHidden: "{{count}} unchanged entries hidden",
        editFieldAria: "Edit {{field}}",
        doneEditingAria: "Done editing {{field}}",
        rejectConfirmTitle: "Reject {{name}}?",
        rejectConfirmBody:
          "There is no approved value to fall back to, so this will remove it from the semantic model.",
        rejectConfirmConfirm: "Reject and remove",
        rejectConfirmCancel: "Cancel",
      },
      candidates: {
        addAction: "Add candidate",
        addFirst: "Add your first candidate",
        addTitle: "Add candidate",
        editTitle: "Edit candidate",
        viewTitle: "Candidate details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        deleteAria: "Delete {{name}}",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save",
        saveFailed: "Could not save candidate changes. Please try again.",
        metricsRequired: "Select at least one metric.",
        businessQuestionRequired: "Business question is required.",
        relationshipTypeNone: "None",
        missingBusinessContext:
          "Add your company and dataset context to get more relevant, better-prioritized candidates.",
        missingBusinessContextAction: "Add business context",
        fields: {
          metrics: "Metrics",
          relationshipType: "Relationship type",
          businessQuestion: "Business question",
          derivedName: "Derived name",
          formula: "Formula",
          formulaPlaceholder: "revenue / orders",
          dimensions: "Dimensions",
          priority: "Priority",
          reasonForRunning: "Reason for running",
        },
        relationshipTypeOptions: {
          ratio: "Ratio",
          correlation: "Correlation",
          delta: "Delta",
          divergence: "Divergence",
          funnel: "Funnel",
          trend: "Trend",
          cluster: "Cluster",
        },
        priorityOptions: {
          high: "High",
          medium: "Medium",
          low: "Low",
        },
        priorityChip: {
          high: "High priority",
          medium: "Medium priority",
          low: "Low priority",
        },
        statusChip: {
          proposed: "Proposed",
          approved: "Approved",
          dismissed: "Dismissed",
        },
      },
      sliceBehavior: {
        sectionTitle: "Slice behavior",
        sectionHint:
          "Configure how pre-aggregated warehouse rows (totals) are filtered for overall metrics vs breakdowns.",
        aggregateValuesHint:
          "Comma-separated codes that represent rolled-up totals",
        defaultValueHint:
          "Code applied when the user does not filter on this slice",
        defaultFilterHint:
          "Optional SQL override instead of default value equality",
        fields: {
          aggregateValues: "Aggregate values",
          defaultValue: "Default value",
          defaultFilter: "Default filter",
          excludeOnBreakdown: "Exclude aggregate values on breakdown",
        },
      },
    },
    dataQuality: {
      tab: "Data Quality",
      integrity: {
        title: "Integrity check",
        description:
          "Checks that relationships and rules still point at entities and fields present in the current model.",
        runAction: "Run check",
        notRunYet: "Not run yet.",
        ok: "No integrity issues found.",
      },
      reconciliation: {
        title: "Reconciliation checks",
        description:
          "Compares a semantic-model-derived value against an independently-computed value.",
        newAction: "New check",
        runAction: "Run",
        empty: "No reconciliation checks configured yet.",
        neverRun: "Never run yet.",
        toleranceChip: "±{{tolerance}}%",
        resultSummary: "Primary: {{primary}} · Independent: {{independent}}",
      },
      newCheckModal: {
        title: "New reconciliation check",
        closeAria: "Close dialog",
        fields: {
          name: "Name",
          metricKey: "Metric key",
          primarySql: "Primary SQL",
          independentSql: "Independent-source SQL",
          tolerance: "Tolerance",
          toleranceHint:
            "Fraction of the independent value, e.g. 0.05 = within 5%",
        },
        validation: {
          required: "This field is required.",
          invalidTolerance: "Enter a tolerance of 0 or greater.",
        },
        actions: {
          cancel: "Cancel",
          create: "Create check",
        },
        errors: {
          saveFailed:
            "Could not create the reconciliation check. Please try again.",
        },
      },
    },
  },
  connections: {
    loadFailed: "Could not load connections.",
    menuAria: "Actions for {{name}}",
    editAction: "Edit",
    deleteAction: "Delete",
  },
  connectionDetail: {
    backAria: "Back to connections",
    notFound: "Connection not found",
    loading: "Loading connection",
    tabs: {
      aria: "Connection detail sections",
      schemaBrowser: "Schema Browser",
      files: "Files",
    },
    schemaBrowser: {
      tablesTitle: "Tables ({{count}})",
      schemaCount: "{{count}} schemas",
      tableCount: "{{count}} tables",
      searchPlaceholder: "Search databases, schemas and tables",
      searchAria: "Search databases, schemas and tables",
      noSearchResults: "No databases, schemas or tables match your search.",
      loading: "Loading tables",
      loadError: "Could not load tables",
      emptyTables: "No tables found",
      rowCountPlaceholder: "— rows",
      rowsLabel: "{{count}} rows",
      unsupportedConnectionType:
        "Schema browser is only available for Snowflake and Cube connections",
      emptyTitle: "Select a table to view schema",
      emptySubtitle:
        "Choose a table from the sidebar to see its columns and sample data",
      columnsTitle: "Columns ({{count}})",
      sampleDataTitle: "Sample Data (First 3 rows)",
      columnName: "Column Name",
      columnType: "Type",
      nullable: "Nullable",
      primaryKey: "Primary Key",
      foreignKey: "Foreign Key",
      nullableYes: "Yes",
      nullableNo: "No",
      pkFkPlaceholder: "-",
      emptyCell: "-",
      sampleLoading: "Loading sample data",
      sampleLoadError: "Could not load sample data",
    },
    objectBrowser: {
      title: "Files",
      loading: "Loading files",
      loadError: "Could not load files",
      empty: "This folder is empty",
      emptyBucket: "No files or folders found",
      expand: "Expand {{name}}",
      collapse: "Collapse {{name}}",
      loadMore: "Load more",
      retry: "Retry",
      download: "Download {{name}}",
      downloadFailed: "Could not download file",
      unsupportedConnectionType:
        "File browsing is only available for S3 connections",
    },
  },
  connectionType: {
    snowflake: "Snowflake",
    cube: "Cube",
    mysql: "MySQL",
    s3: "Amazon S3",
  },
  newConnectionModal: {
    title: "New Connection",
    closeAria: "Close dialog",
    tabs: {
      aria: "Connection type",
    },
    fields: {
      name: "Connection Name",
      account: "Account Identifier",
      warehouse: "Warehouse",
      database: "Database",
      user: "User",
      role: "Role (optional)",
      roleHelper: "Leave blank to use the Snowflake user's default role",
      host: "Host",
      port: "Port",
      authMethod: "Auth Method",
      authType: "Auth Type",
      privateKey: "Private Key",
      privateKeyPassphrase: "Private Key Passphrase",
      password: "Password",
      tokenUrl: "Token URL",
      clientId: "Client ID",
      clientSecret: "Client Secret",
      baseUrl: "Base URL",
      bucket: "Bucket Name",
      region: "AWS Region",
      prefix: "Folder Path (optional)",
      roleArn: "IAM Role ARN (optional)",
      proxySection: "Proxy (optional)",
      proxyHost: "Proxy Host",
      proxyHostHelper:
        "For connecting through a tunnel or IP the warehouse has whitelisted",
      proxyPort: "Proxy Port",
    },
    hints: {
      bucket:
        "If you are not using an IAM role below, grant s3:ListBucket to Olive's IAM roles in this bucket's policy.",
      prefix:
        "Limits this connection to one folder. Required if your bucket policy only grants access to a specific prefix.",
      roleArn:
        "Leave blank to use Olive's own IAM role via your bucket policy. To have Olive assume a role in your account instead, paste its ARN -- its trust policy must allow both of Olive's roles.",
    },
    authMethod: {
      privateKey: "Private Key",
      password: "Password",
    },
    authType: {
      oauth2ClientCredentials: "OAuth2 Client Credentials",
    },
    actions: {
      testConnection: "Test Connection",
      testingConnection: "Testing...",
      cancel: "Cancel",
      saveConnection: "Save Connection",
    },
    validation: {
      required: "This field is required.",
      invalid: "Enter a valid value.",
    },
    errors: {
      saveFailed: "Could not save the connection. Please try again.",
      testFailed: "Could not test the connection. Please try again.",
    },
    testSuccess: "Connection successful.",
  },
  editConnectionModal: {
    title: "Edit Connection",
    closeAria: "Close dialog",
    testDisabledHint:
      "Enter a new credential above to test this connection before saving.",
    actions: {
      cancel: "Cancel",
      saveChanges: "Save Changes",
    },
    errors: {
      loadFailed: "Could not load this connection. Please try again.",
      saveFailed: "Could not save the connection. Please try again.",
      testFailed: "Could not test the connection. Please try again.",
      unsupportedType: "This connection type cannot be edited yet.",
    },
  },
  newDomainModal: {
    title: "New Domain",
    closeAria: "Close dialog",
    mode: {
      create: "Create new",
      import: "Import from file",
    },
    import: {
      chooseFile: "Choose export file",
      required: "Choose an exported domain file to import.",
      invalidFile: "This file isn't a valid domain export.",
    },
    fields: {
      name: "Name",
      description: "Description",
      connection: "Connection",
    },
    actions: {
      cancel: "Cancel",
      addDomain: "Add Domain",
      importDomain: "Import Domain",
    },
    validation: {
      required: "This field is required.",
    },
    errors: {
      saveFailed: "Could not save the domain. Please try again.",
    },
  },
} as const;
